/**
 * Grouping Strategy Registry
 *
 * Jeder Gruppierungsmodus ist ein deklaratives Objekt.
 * Neuen Modus hinzufügen = ein Objekt hier eintragen.
 *
 * @typedef {Object} GroupingStrategy
 * @property {string} key - Eindeutiger Schlüssel
 * @property {string} label - Anzeigename im Dropdown
 * @property {(card: Object) => string} groupFn - Karte → Gruppenname
 * @property {((a: string, b: string) => number)|null} sortGroupsFn - Sortierung der Gruppen (null = User-definiert)
 * @property {boolean} persistent - Reihenfolge wird im Backend gespeichert
 * @property {boolean} editable - Karten per DnD verschiebbar, Gruppen löschbar/anlegbar
 */

// WUBRG-Sortierung für Farben
const WUBRG_ORDER = { W: 0, U: 1, B: 2, R: 3, G: 4 };

function colorSortValue(combo) {
  if (combo === 'Colorless') return [99, 0];
  const letters = combo.split('');
  return [letters.length, Math.min(...letters.map(c => WUBRG_ORDER[c] ?? 98))];
}

export const GROUPING_STRATEGIES = {
  categories: {
    key: 'categories',
    label: 'Kategorien',
    groupFn: (card) => {
      return card.category || 'other';
    },
    sortGroupsFn: null,
    persistent: true,
    editable: true,
  },
  types: {
    key: 'types',
    label: 'Typen',
    groupFn: (card) => {
      if (card.types && card.types.length > 0) {
        return card.types[0].toLowerCase();
      }
      return 'other';
    },
    sortGroupsFn: (a, b) => a.localeCompare(b),
    persistent: false,
    editable: false,
  },
  cmc: {
    key: 'cmc',
    label: 'Manakosten',
    groupFn: (card) => String(card.cmc ?? 0),
    sortGroupsFn: (a, b) => Number(a) - Number(b),
    persistent: false,
    editable: false,
  },
  color: {
    key: 'color',
    label: 'Farbe',
    groupFn: (card) => {
      const colors = card.colorIdentity;
      if (!colors || colors.length === 0) return 'Colorless';
      return [...colors].sort().join('');
    },
    sortGroupsFn: (a, b) => {
      const [aLen, aFirst] = colorSortValue(a);
      const [bLen, bFirst] = colorSortValue(b);
      if (aLen !== bLen) return aLen - bLen;
      return aFirst - bFirst;
    },
    persistent: false,
    editable: false,
  },
  collectionStatus: {
    key: 'collectionStatus',
    label: 'Sammlungsstatus',
    groupFn: (card) => card.isInCollection === false ? 'Fehlt' : 'Vorhanden',
    sortGroupsFn: (a) => a === 'Vorhanden' ? -1 : 1,
    persistent: false,
    editable: false,
  },
  power: {
    key: 'power',
    label: 'Stärke',
    groupFn: (card) => card.power ?? '—',
    sortGroupsFn: (a, b) => (parseInt(a) || 0) - (parseInt(b) || 0),
    persistent: false,
    editable: false,
  },
  toughness: {
    key: 'toughness',
    label: 'Widerstand',
    groupFn: (card) => card.toughness ?? '—',
    sortGroupsFn: (a, b) => (parseInt(a) || 0) - (parseInt(b) || 0),
    persistent: false,
    editable: false,
  },
};

/**
 * Gibt die Strategy für einen key zurück, mit Fallback auf 'categories'.
 */
export function getStrategy(key) {
  return GROUPING_STRATEGIES[key] || GROUPING_STRATEGIES.categories;
}
